type SortItem = string | number | Record<string, any>;

interface OrderPart {
  paths: string[];
  order: 'asc' | 'desc';
}

interface SortResult<T> {
  success: boolean;
  result: T[];
}

export const ArrayNaturalSort = {
  naturalSort<T extends SortItem>(array: T[], mainOrder: 'asc' | 'desc' = 'asc', orderArray: OrderPart[] = []): SortResult<T> {
    if (!Array.isArray(array) || array.length === 0) {
      return { success: false, result: array };
    }

    // Validate orderArray and orders
    const validatedOrderArray: OrderPart[] = orderArray
      .map((orderPart): OrderPart | null => {
        if (!orderPart.paths || !Array.isArray(orderPart.paths) || orderPart.paths.length === 0) {
          return null;
        }
        const order = ['asc', 'desc'].includes(orderPart.order.toLowerCase())
          ? orderPart.order.toLowerCase() as 'asc' | 'desc'
          : mainOrder;
        return { paths: orderPart.paths, order };
      })
      .filter((part): part is OrderPart => part !== null);

    // If no valid paths, use empty path for flat array sorting
    const finalOrderArray: OrderPart[] = validatedOrderArray.length > 0
      ? validatedOrderArray
      : [{ paths: [], order: mainOrder }];

    // Clone array to avoid mutating original
    const sortedArray = [...array].sort((a: T, b: T): number => {
      for (const { paths, order } of finalOrderArray) {
        const multiplier: number = order === 'asc' ? 1 : -1;
        const valA: string = this.getSortValueByPath(a, paths);
        const valB: string = this.getSortValueByPath(b, paths);

        // Skip array comparisons for now
        if (Array.isArray(valA) && Array.isArray(valB)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        // Check if all values for this path are Roman numerals
        if (this.isAllRomansSort(array, paths)) {
          const result: number = (this.romanToInt(valA) - this.romanToInt(valB)) * multiplier;
          if (result !== 0) return result;
          // eslint-disable-next-line no-continue
          continue;
        }

        // Check if values are numeric
        const isNumericA: boolean = !Number.isNaN(parseFloat(valA)) && Number.isFinite(Number(valA));
        const isNumericB: boolean = !Number.isNaN(parseFloat(valB)) && Number.isFinite(Number(valB));

        // Case 1: Both non-numeric (strings)
        if (!isNumericA && !isNumericB) {
          const result: number = valA.localeCompare(valB) * multiplier;
          if (result !== 0) return result;
          // eslint-disable-next-line brace-style
        }
        // Case 2: Both numeric
        else if (isNumericA && isNumericB) {
          const result: number = (parseFloat(valA) - parseFloat(valB)) * multiplier;
          if (result !== 0) return result;
          // eslint-disable-next-line brace-style
        }
        // Case 3: One numeric, one not
        else {
          const result: number = (isNumericA ? 1 : -1) * multiplier;
          if (result !== 0) return result;
        }
      }
      return 0;
    });

    return { success: true, result: sortedArray };
  },

  isValidRoman(str: any): boolean {
    if (typeof str !== 'string') return false;
    return /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/.test(str);
  },

  romanToInt(roman: string): number {
    if (!this.isValidRoman(roman)) return 0;
    const romans: Record<string, number> = {
      M: 1000,
      CM: 900,
      D: 500,
      CD: 400,
      C: 100,
      XC: 90,
      L: 50,
      XL: 40,
      X: 10,
      IX: 9,
      V: 5,
      IV: 4,
      I: 1,
    };
    let num: number = 0;
    let i: number = 0;
    while (i < roman.length) {
      if (i + 1 < roman.length && romans[roman[i] + roman[i + 1]]) {
        num += romans[roman[i] + roman[i + 1]];
        i += 2;
      } else {
        num += romans[roman[i]];
        // eslint-disable-next-line no-plusplus
        i++;
      }
    }
    return num;
  },

  isAllRomansSort<T extends SortItem>(array: T[], paths: string[]): boolean {
    return array.every((item: T): boolean => {
      const value: string = this.getSortValueByPath(item, paths);
      return this.isValidRoman(value);
    });
  },

  getSortValueByPath<T extends SortItem>(item: T, paths: string[] = []): string {
    if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
      let current: any = item;
      for (const key of paths) {
        if (current && typeof current === 'object' && key in current) {
          current = current[key];
        } else {
          return '';
        }
      }
      return typeof current === 'string' || typeof current === 'number' ? String(current) : '';
    }
    return typeof item === 'string' || typeof item === 'number' ? String(item) : '';
  },
};
